import { api } from "./api";

const GA4_ID = process.env.REACT_APP_GA4_ID;
// The production pixel fires ONLY on the production host. Any other host (localhost,
// preview, staging) uses the dedicated TEST pixel ID when one is configured — never the
// production one. Empty test ID = pixel and CAPI are fully blocked off-production.
const PROD_HOST = "www.decodemydenial.com";
const HOST = typeof window !== "undefined" ? window.location.hostname : "";
const PIXEL_ID = HOST === PROD_HOST ? process.env.REACT_APP_META_PIXEL_ID : process.env.REACT_APP_META_PIXEL_ID_TEST;
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const ADS_LABEL = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;
const ADS_PURCHASE_EVENT = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_EVENT;

let initialized = false;
let metaParamsPromise = null;

// Meta CAPI Parameter Builder (client library) — captures/persists _fbp and _fbc on
// landing-page load (Meta warns against down-funnel-only retrieval). Writes first-party
// cookies only; nothing reaches Meta until marketing consent gates the pixel/CAPI send.
// Blocked entirely on non-production hosts without a configured test pixel.
export function processMetaParams() {
  if (metaParamsPromise) return metaParamsPromise;
  if (!PIXEL_ID) return Promise.resolve(null);
  const builder = window.clientParamBuilder;
  if (!builder) return Promise.resolve(null);
  const getIpFn = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/client-ip`, { credentials: "same-origin" });
      if (!r.ok) return null;
      const d = await r.json();
      return d.ipv6 || d.ipv4 || null; // IPv6 preferred over IPv4
    } catch { return null; }
  };
  metaParamsPromise = builder.processAndCollectAllParams(window.location.href, getIpFn).catch(() => null);
  return metaParamsPromise;
}

// Exact cookie values — _fbc is case-sensitive; never normalize or lowercase it.
export function getMetaClickIds() {
  const b = window.clientParamBuilder;
  if (!b) return { fbc: null, fbp: null };
  try { return { fbc: b.getFbc(), fbp: b.getFbp() }; } catch { return { fbc: null, fbp: null }; }
}

export function getConsent() {
  try { return JSON.parse(localStorage.getItem("dd_consent")); } catch { return null; }
}

export function setConsent(c) {
  localStorage.setItem("dd_consent", JSON.stringify({ ...c, at: new Date().toISOString(), v: 1 }));
}

export function hasMarketingConsent() {
  return !!getConsent()?.marketing;
}

export function initAnalytics() {
  if (initialized) return;
  captureUtm();
  processMetaParams(); // _fbp/_fbc captured at landing, not down-funnel
  const consent = getConsent();
  if (!consent) return; // consent banner not answered yet — load nothing non-essential
  initialized = true;
  if (GA4_ID && consent.analytics) {
    // Injected only after analytics consent — never shipped in index.html.
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA4_ID, { cookie_flags: "SameSite=Lax;Secure" });
    if (ADS_ID) window.gtag("config", ADS_ID); // register the Google Ads tag so conversions are attributed
  }
  if (PIXEL_ID && consent.marketing) {
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("init", PIXEL_ID);
    window.__ddPvId = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : "pv-" + Date.now() + "-" + Math.random().toString(36).slice(2);
    window.fbq("track", "PageView", {}, { eventID: window.__ddPvId });
    // Server-side CAPI sends only server-verified events (Purchase via Stripe webhook,
    // Lead via account creation) — browser-claimed events are never relayed.
  }
}

export function captureUtm() {
  if (localStorage.getItem("dd_utm")) return;
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  localStorage.setItem("dd_utm", JSON.stringify(utm));
}

export const getUtm = () => JSON.parse(localStorage.getItem("dd_utm") || "{}");

export const newEventId = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// GA4 recommended-event aliases — fires alongside the internal event so these
// funnel steps can be marked as key events (conversions) in GA4. Consent-gated:
// window.gtag only exists after analytics consent (see initAnalytics).
const GA4_ALIAS = {
  sign_up: "sign_up",
  unlock_viewed: "begin_checkout",
  followup_checkout_started: "begin_checkout",
  mail_order_started: "begin_checkout",
  purchase: "purchase",
  followup_purchased: "purchase",
  mail_order_paid: "purchase",
};

const FB_MAP = { preview_shown: "Lead", paywall_view: "InitiateCheckout", unlock_viewed: "InitiateCheckout", purchase: "Purchase", followup_purchased: "Purchase", mail_order_paid: "Purchase" };

export function track(name, { claimType, eventId, value, ...rest } = {}) {
  const params = { ...rest, ...getUtm() };
  if (claimType) params.claim_type = claimType;
  if (window.gtag) {
    const p = value != null ? { ...params, value, currency: "USD" } : params;
    // Purchase-family events carry transaction_id on the PRIMARY event too — the alias
    // branch below is skipped when alias === name (e.g. purchase→purchase), so without
    // this the GA4 purchase would never reconcile with the Ads conversion.
    const PURCHASE_FAMILY = name === "purchase" || name === "followup_purchased" || name === "mail_order_paid";
    const pp = (PURCHASE_FAMILY && eventId) ? { ...p, transaction_id: eventId } : p;
    window.gtag("event", name, pp);
    const alias = GA4_ALIAS[name];
    if (alias && alias !== name) {
      const ap = (alias === "purchase" && eventId) ? { ...p, transaction_id: eventId } : p;
      window.gtag("event", alias, ap);
    }
  }
  if (window.fbq) {
    const fbName = FB_MAP[name];
    if (fbName) {
      const opts = eventId ? { eventID: eventId } : undefined;
      window.fbq("track", fbName, value ? { value, currency: "USD" } : {}, opts);
    }
  }
}

// Google Ads purchase conversion. Consent-gated exactly like GA4: window.gtag only exists
// after analytics consent, so this never fires pre-consent. Uses the SAME transaction_id as
// the GA4 purchase event (the shared eventId) so the two reconcile. Enhanced conversions:
// the raw email goes in user_data — gtag.js normalizes and SHA-256 hashes it client-side
// before anything is sent. When ADS_LABEL is set, the classic send_to conversion format is
// used; otherwise the custom event name (manual_event_PURCHASE) is what Google Ads matches.
export function trackAdsConversion({ transactionId, value, email } = {}) {
  if (!ADS_ID || !window.gtag || value == null) return;
  const params = { value, currency: "USD", transaction_id: transactionId };
  if (ADS_LABEL) params.send_to = `${ADS_ID}/${ADS_LABEL}`;
  if (email) params.user_data = { email };
  window.gtag("event", ADS_PURCHASE_EVENT, params);
}

// In-app product events → backend analytics_events (powers the admin workflow funnel).
// Fire-and-forget, never toasts, never throws.
export function trackCase(event, data = {}) {
  api.post("/analytics/event", { event, data }, { silentError: true }).catch(() => {});
}
